import React, { useEffect, useRef, useState } from "react";
import { AppLayout } from "src/components/layouts";
import { useParams } from "react-router-dom";
import AgentTable from "./components/AgentTable";
import AdminService from "src/services/admin.services";
import BackButton from "src/components/BackButton";
import SearchInput from "src/components/ui/SearchInput";
import { QueryFunctionContext, useQuery } from "react-query";

const adminService = new AdminService();
const itemsPerPage = 100;

const fetchAgentsQuery = async (ctx: QueryFunctionContext) => {
  const [_key, currentPage, itemsPerPage, searchTerm] = ctx.queryKey;
  const response = await adminService.getAgents(
    currentPage as number,
    (itemsPerPage as number).toString(),
    searchTerm as string
  );
  if (!Array.isArray(response)) {
    throw new Error("Unexpected response format");
  }
  return response;
};

const AgentManagement: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState<boolean>(false);
  // const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [filter, setFilter] = useState("all");
  // const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "active">("all");

  const shouldAutoRefresh = !searchTerm.trim() && filter === "all";
  const {
    data: agents = [],
    isLoading,
    isError,
    error,
  } = useQuery(
    ["agents", currentPage, itemsPerPage, searchTerm],
    fetchAgentsQuery,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !!currentPage,
      refetchInterval: 60000,
      onSuccess: (response) => {
        setData(response);
        if (!shouldAutoRefresh) {
          setFilteredData(response);
        }
        // calculate total pages based on returned length
        if (response.length < itemsPerPage) {
          setTotalPages(currentPage);
        } else {
          setTotalPages(currentPage + 1);
        }
      },
      onError: () => {
        // Error fetching agents - silently fail
      },
    }
  );
  // useEffect(() => {
  //   fetchAgents();

  //   let interval: NodeJS.Timeout | undefined;

  //   if (!shouldAutoRefresh) {
  //     interval = setInterval(fetchAgents, 60000); // 60 seconds
  //   }

  //   return () => {
  //     if (interval) clearInterval(interval);
  //   };
  // }, [currentPage, shouldAutoRefresh]);

  useEffect(() => {
    const filterAgents = () => {
      let filtered = [...data];
      const now = new Date();

      // Tab-based filtering
      if (activeTab === "active") {
        // Only agents who logged in within the last 7 days
        filtered = filtered.filter((agent) => {
          if (!agent.last_login) return false;
          const lastLogin = new Date(agent.last_login);
          const diffDays =
            (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24);
          return diffDays <= 7;
        });
      }

      switch (filter) {
        case "day":
          filtered = filtered.filter((agent) => {
            if (!agent.last_login) return false;
            const date = new Date(agent.last_login);
            return (
              date.getDate() === now.getDate() &&
              date.getMonth() === now.getMonth() &&
              date.getFullYear() === now.getFullYear()
            );
          });
          break;
        case "week":
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          filtered = filtered.filter((agent) => {
            if (!agent.last_login) return false;
            const date = new Date(agent.last_login);
            return date >= startOfWeek && date <= now;
          });
          break;
        case "month":
          filtered = filtered.filter((agent) => {
            if (!agent.last_login) return false;
            const date = new Date(agent.last_login);
            return (
              date.getMonth() === now.getMonth() &&
              date.getFullYear() === now.getFullYear()
            );
          });
          break;
        case "year":
          filtered = filtered.filter((agent) => {
            if (!agent.last_login) return false;
            const date = new Date(agent.last_login);
            return date.getFullYear() === now.getFullYear();
          });
          break;
        default:
          // "all" case - no additional filtering
          break;
      }

      setFilteredData(filtered);
    };

    filterAgents();
  }, [data, filter, activeTab]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setSearching(term.trim() !== "");
  };

  return (
    <AppLayout>
      <div className="container mx-auto px-2 py-4">
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-2">
            {/* Tabs for All Agents / Active Agents */}
            <div className="mb-4 flex space-x-2">
              {["all", "active"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as typeof activeTab)}
                  className={`py-2 px-4 rounded-md transition duration-300 
                    ${
                      activeTab === tab
                        ? "bg-amber-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }
                    hover:bg-amber-400`}
                >
                  {tab === "all" ? "All Agents" : "Active Agents"}
                </button>
              ))}
            </div>
            {/* End Tabs */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
              <div className="flex items-center gap-4">
                <BackButton />
                <h2 className="text-2xl font-bold text-amber-900">
                  Agent Management
                </h2>
              </div>
              <div className="w-full md:w-80">
                <SearchInput
                  search={handleSearch}
                  placeholder="Search by first or last name..."
                />
              </div>
            </div>

            {error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-500"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      {(error as Error).message}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <AgentTable
                data={filteredData}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setSearching={setSearching}
                filter={filter}
                setFilter={setFilter}
                loading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default AgentManagement;
